import { useMemo, useState, useEffect } from 'react';
import {
  Award,
  Search,
  Info,
  Plus,
  BarChart3,
  CheckCircle2,
  Clock,
  FileText,
} from 'lucide-react';
import type { TipoAvaliacao, Avaliacao } from '../../api/hooks/useAvaliacoes';
import { TIPO_LABELS, STATUS_LABELS, metaAvaliacao } from './avaliacoes-professor-shared';
import { AvaliacaoDetalhePanel } from './AvaliacaoDetalhePanel';
import { AvaliacoesFormBlock } from './AvaliacoesFormBlock';
import { AvaliacoesNotasSheet } from './AvaliacoesNotasSheet';
import { Modal } from '../../components/ui/Modal';
import type { AvaliacoesProfessorPageContext } from './useAvaliacoesProfessorPage';
import './avaliacoes-professor-desktop.css';
import './professor-dashboard-desktop.css';

interface Props {
  ctx: AvaliacoesProfessorPageContext;
}

export function AvaliacoesProfessorDesktop({ ctx }: Props) {
  const {
    turmaId,
    setTurmaId,
    materiaId,
    setMateriaId,
    bimestre,
    setBimestre,
    showForm,
    setShowForm,
    detalheId,
    setDetalheId,
    avaliacaoDetalhe,
    avaliacoes,
    isLoading,
    minhasTurmas,
    minhasMaterias,
    stats,
    historicoNotasQuery,
    abrirLancarNotas,
    handleMudarStatus,
    mudarStatus,
    formData,
    handleCriar,
    criar,
  } = ctx;

  const [busca, setBusca] = useState('');
  const [filtroTipo, setFiltroTipo] = useState<TipoAvaliacao | ''>('');

  const filtered = useMemo(() => {
    let list = avaliacoes;
    if (filtroTipo) list = list.filter((a) => a.tipo === filtroTipo);
    const q = busca.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (a) =>
          a.titulo.toLowerCase().includes(q) ||
          (a.turma?.nome || '').toLowerCase().includes(q) ||
          (a.materia?.nome || '').toLowerCase().includes(q),
      );
    }
    return [...list].sort((a, b) => {
      const pa = a.status === 'RASCUNHO' || a.status === 'AGENDADA' || a.status === 'EM_APLICACAO';
      const pb = b.status === 'RASCUNHO' || b.status === 'AGENDADA' || b.status === 'EM_APLICACAO';
      if (pa && !pb) return -1;
      if (!pa && pb) return 1;
      return (b.criadoEm || '').localeCompare(a.criadoEm || '');
    });
  }, [avaliacoes, busca, filtroTipo]);

  useEffect(() => {
    if (filtered.length === 0) {
      setDetalheId(null);
      return;
    }
    if (!detalheId || !filtered.some((a) => a.id === detalheId)) {
      setDetalheId(filtered[0].id);
    }
  }, [filtered, detalheId, setDetalheId]);

  const renderListItem = (av: Avaliacao) => {
    const tipoInfo = TIPO_LABELS[av.tipo];
    const statusInfo = STATUS_LABELS[av.status];
    const active = detalheId === av.id;
    const pendente =
      av.status === 'RASCUNHO' || av.status === 'AGENDADA' || av.status === 'EM_APLICACAO';

    return (
      <button
        key={av.id}
        type="button"
        className={[
          'aval-desk-list-item',
          active ? 'aval-desk-list-item--active' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        style={{ borderLeftColor: pendente ? tipoInfo.color : '#e5e7eb' }}
        onClick={() => setDetalheId(av.id)}
      >
        <div
          className="aval-desk-list-icon"
          style={{ background: `${tipoInfo.color}18`, color: tipoInfo.color }}
        >
          {tipoInfo.icon}
        </div>
        <div className="aval-desk-list-main">
          <p className="aval-desk-list-title">{av.titulo}</p>
          <p className="aval-desk-list-meta">{metaAvaliacao(av)}</p>
        </div>
        <div className="aval-desk-list-aside">
          <span
            className="aval-item-status"
            style={{ background: statusInfo.bg, color: statusInfo.color, fontSize: '0.52rem' }}
          >
            {statusInfo.label}
          </span>
          <span className="aval-desk-list-notas">{av._count?.notas ?? 0} notas</span>
        </div>
      </button>
    );
  };

  return (
    <>
      <div className="admin-page text-[13px] leading-snug dash-desk-page aval-desk-page">
        <div className="dash-desk-root aval-desk-root">
          <div className="dash-desk-top aval-desk-top">
            <div className="dash-desk-greeting">
              <h1 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: '#0f172a' }}>
                Avaliacoes
              </h1>
              <p className="aval-desk-subtitle">
                Provas e trabalhos das suas turmas · edicao em ate 7 dias
              </p>
            </div>
            <Award size={18} style={{ color: '#7c3aed', opacity: 0.85 }} aria-hidden />
          </div>

          <div className="aval-desk-metrics">
            <button
              type="button"
              className={`aval-desk-kpi-btn${!filtroTipo ? ' aval-desk-kpi-btn--active' : ''}`}
              onClick={() => setFiltroTipo('')}
            >
              <div className="dash-desk-kpi">
                <div className="dash-desk-kpi-icon" style={{ background: '#f5f3ff', color: '#7c3aed' }}>
                  <BarChart3 size={15} />
                </div>
                <div>
                  <div className="dash-desk-kpi-val">{stats.total}</div>
                  <div className="dash-desk-kpi-lbl">Total</div>
                </div>
              </div>
            </button>
            <button
              type="button"
              className="aval-desk-kpi-btn"
              onClick={() => setFiltroTipo('')}
            >
              <div className="dash-desk-kpi">
                <div className="dash-desk-kpi-icon" style={{ background: '#f0fdf4', color: '#059669' }}>
                  <CheckCircle2 size={15} />
                </div>
                <div>
                  <div className="dash-desk-kpi-val">{stats.concluidas}</div>
                  <div className="dash-desk-kpi-lbl">Concluidas</div>
                </div>
              </div>
            </button>
            <button
              type="button"
              className="aval-desk-kpi-btn"
              onClick={() => {
                setFiltroTipo('');
                setDetalheId(
                  avaliacoes.find(
                    (a) =>
                      a.status === 'RASCUNHO' ||
                      a.status === 'AGENDADA' ||
                      a.status === 'EM_APLICACAO',
                  )?.id ?? null,
                );
              }}
            >
              <div className="dash-desk-kpi">
                <div className="dash-desk-kpi-icon" style={{ background: '#fffbeb', color: '#d97706' }}>
                  <Clock size={15} />
                </div>
                <div>
                  <div className="dash-desk-kpi-val">{stats.pendentes}</div>
                  <div className="dash-desk-kpi-lbl">Pendentes</div>
                </div>
              </div>
            </button>
            <div className="dash-desk-kpi">
              <div className="dash-desk-kpi-icon" style={{ background: '#eff6ff', color: '#2563eb' }}>
                <FileText size={15} />
              </div>
              <div>
                <div className="dash-desk-kpi-val">{minhasTurmas.length}</div>
                <div className="dash-desk-kpi-lbl">Turmas grade</div>
              </div>
            </div>
          </div>

          <div className="aval-desk-notice">
            <Info size={14} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>
              Turmas e materias da <strong>sua grade</strong>. Notas dos alunos via{' '}
              <code style={{ fontSize: '0.65rem' }}>chamadas/turma/…/alunos</code>.
            </span>
          </div>

          <div className="aval-desk-toolbar">
            <div className="aval-desk-search-wrap">
              <Search size={14} />
              <input
                type="search"
                className="aval-desk-search"
                placeholder="Buscar titulo, turma, materia…"
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
              />
            </div>
            <select
              className="aval-desk-filter"
              value={turmaId}
              onChange={(e) => setTurmaId(e.target.value)}
              aria-label="Turma"
            >
              <option value="">Todas turmas</option>
              {minhasTurmas.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.nome}
                </option>
              ))}
            </select>
            <select
              className="aval-desk-filter"
              value={materiaId}
              onChange={(e) => setMateriaId(e.target.value)}
              aria-label="Materia"
            >
              <option value="">Todas materias</option>
              {minhasMaterias.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.nome}
                </option>
              ))}
            </select>
            <select
              className="aval-desk-filter"
              value={bimestre}
              onChange={(e) => setBimestre(Number(e.target.value))}
              aria-label="Bimestre"
            >
              <option value={0}>Todos bimestres</option>
              {[1, 2, 3, 4].map((b) => (
                <option key={b} value={b}>
                  {b}º Bim
                </option>
              ))}
            </select>
            <button type="button" className="aval-desk-btn-nova" onClick={() => setShowForm(true)}>
              <Plus size={14} />
              Nova
            </button>
            <span className="aval-desk-count">{filtered.length} avaliacao(oes)</span>
          </div>

          <div className="aval-desk-body">
            <div className="aval-desk-list-card">
              <div className="aval-desk-list-scroll">
                {isLoading ? (
                  <p className="aval-desk-loading">Carregando avaliacoes…</p>
                ) : filtered.length === 0 ? (
                  <p className="aval-desk-loading">Nenhuma avaliacao encontrada.</p>
                ) : (
                  filtered.map(renderListItem)
                )}
              </div>
            </div>

            <div className="aval-desk-detail-card">
              <div className="aval-desk-detail-scroll">
                {avaliacaoDetalhe ? (
                  <AvaliacaoDetalhePanel
                    av={avaliacaoDetalhe}
                    historicoNotasQuery={historicoNotasQuery}
                    onLancarNotas={abrirLancarNotas}
                    onMudarStatus={handleMudarStatus}
                    mudarStatusPending={mudarStatus.isPending}
                    variant="panel"
                  />
                ) : (
                  <div className="aval-desk-detail-empty">
                    Selecione uma avaliacao para ver dados, status e lancar notas.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        open={showForm}
        onClose={() => setShowForm(false)}
        title="Nova avaliacao"
        size="lg"
        contentClassName="aval-desk-nova-modal"
        dialogId="modal-nova-avaliacao"
        footer={
          <div className="aval-desk-modal-footer">
            <button
              type="button"
              className="aval-form-btn-secondary"
              onClick={() => setShowForm(false)}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="aval-form-btn-primary"
              onClick={handleCriar}
              disabled={
                !formData.titulo || !formData.turmaId || !formData.materiaId || criar.isPending
              }
            >
              {criar.isPending ? 'Salvando...' : 'Criar avaliacao'}
            </button>
          </div>
        }
      >
        <AvaliacoesFormBlock {...ctx} hideActions />
      </Modal>

      <AvaliacoesNotasSheet {...ctx} />
    </>
  );
}
